import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import BeforeAfterReel from "@/components/BeforeAfterReel";
import JsonLd, { reviewSchema } from "@/components/JsonLd";
import {
  ArrowRight, Hammer, HardHat, Home as HomeIcon, ShieldCheck, MapPin, Phone,
  Star, Sparkles, Compass, Ruler, ChevronRight
} from "lucide-react";

const HERO_IMG = "/hero/corpus-skyline.jpg";
const REMODEL_IMG = "https://images.unsplash.com/photo-1771371282665-545256b20dca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICES = [
  { key: "new_construction", icon: HardHat, title: "New Construction", blurb: "Custom homes built from the ground up across Corpus Christi & the Coastal Bend." },
  { key: "remodel", icon: Hammer, title: "Remodels", blurb: "Kitchens, baths, and full additions — finished on time, finished right." },
  { key: "renovation", icon: HomeIcon, title: "Renovations", blurb: "Whole-home updates, hurricane-rated upgrades, and lasting modernizations." },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [plans, setPlans] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/projects?featured=true").then((r) => setProjects(r.data.slice(0, 3))).catch(() => {});
    api.get("/floor-plans").then((r) => setPlans(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials?featured=true").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {testimonials.map((t) => <JsonLd key={`r-${t.id}`} id={`review-${t.id}`} data={reviewSchema(t)} />)}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Corpus Christi skyline at sunset"
            className="w-full h-full object-cover"
            style={{filter: 'blur(2px) saturate(0.85)', transform: 'scale(1.06)'}}
          />
          {/* Navy color wash to bind the image into the brand palette */}
          <div className="absolute inset-0" style={{background: 'rgba(11, 19, 43, 0.55)', mixBlendMode: 'multiply'}} />
          {/* Strong vertical gradient for text readability */}
          <div className="absolute inset-0" style={{background: 'linear-gradient(180deg, rgba(11,19,43,0.55) 0%, rgba(11,19,43,0.7) 55%, rgba(11,19,43,0.98) 100%)'}} />
          {/* Warm gold glow from the right (echoing the sunset) */}
          <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse 80% 60% at 85% 40%, rgba(212, 163, 115, 0.15), transparent 60%)'}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-32 lg:pt-40 lg:pb-44">
          <div className="badge mb-6 scroll-fade-in" data-testid="hero-badge">
            <MapPin size={12} /> Corpus Christi · Nueces County, TX
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl scroll-fade-in" style={{animationDelay: '0.1s'}}>
            Built for the <span style={{color:'var(--accent)'}}>Texas Gulf Coast.</span><br/>
            Built to last a generation.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed scroll-fade-in" style={{color:'var(--text-muted)', animationDelay: '0.2s'}}>
            Coastal Bend Contractor Co. designs and builds custom new homes, kitchen and bath remodels, and full-home renovations engineered for Gulf Coast living.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 scroll-fade-in" style={{animationDelay: '0.3s'}}>
            <Link to="/contact" className="btn-primary" data-testid="hero-quote-btn">Request a Free Quote <ArrowRight size={16} /></Link>
            <Link to="/floor-plans" className="btn-ghost" data-testid="hero-plans-btn">Browse Floor Plans</Link>
          </div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-3xl scroll-fade-in" style={{animationDelay: '0.4s'}}>
            {[
              {n: "120+", l: "Homes Built"},
              {n: "25", l: "Years on the Coast"},
              {n: "100%", l: "Licensed & Insured"},
              {n: "4.9★", l: "Average Rating"},
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl lg:text-4xl font-bold" style={{color:'var(--accent)'}}>{s.n}</div>
                <div className="text-xs uppercase tracking-[0.2em] mt-1" style={{color:'var(--text-muted)'}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" id="services">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="label-overline mb-4">What we build</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Three crafts. One standard.</h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-base leading-relaxed self-end" style={{color:'var(--text-muted)'}}>
            Every Coastal Bend project — whether a 3,500 sqft custom build or a single bath remodel — is run by the same hands-on team, with weekly client updates and clear, fixed pricing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <Link to="/services" key={s.key} className="card p-7 group" data-testid={`service-${s.key}`}>
              <div className="w-12 h-12 rounded-md flex items-center justify-center mb-6" style={{background: 'rgba(212,163,115,0.1)'}}>
                <s.icon size={22} style={{color: 'var(--accent)'}} />
              </div>
              <h3 className="font-display text-2xl font-bold mb-2">{s.title}</h3>
              <p className="text-sm leading-relaxed" style={{color:'var(--text-muted)'}}>{s.blurb}</p>
              <div className="mt-6 flex items-center gap-2 text-sm font-medium" style={{color:'var(--accent)'}}>
                Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <div className="label-overline mb-3">Recent work</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Featured projects</h2>
          </div>
          <Link to="/projects" className="btn-ghost !py-2 !px-4 !text-sm" data-testid="view-all-projects">View all <ArrowRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.map((p, i) => (
            <Link to={`/projects/${p.id}`} key={p.id} className="card overflow-hidden group" data-testid={`featured-project-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="text-xs uppercase tracking-wider mb-2" style={{color: 'var(--accent)'}}>
                  {p.category.replace('_',' ')}
                </div>
                <h3 className="font-display text-xl font-bold mb-1">{p.title}</h3>
                <div className="text-sm flex items-center gap-1.5" style={{color:'var(--text-muted)'}}>
                  <MapPin size={13}/> {p.location || 'Coastal Bend, TX'}
                </div>
              </div>
            </Link>
          ))}
          {projects.length === 0 && Array.from({length:3}).map((_,i) => <div key={i} className="card aspect-[4/5] animate-pulse" style={{background:'var(--surface)'}}/>) }
        </div>
      </section>

      {/* BEFORE / AFTER REEL */}
      <BeforeAfterReel />

      {/* FLOOR PLANS TEASER */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="label-overline mb-4">For new construction buyers</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Choose your <span style={{color:'var(--accent)'}}>floor plan.</span><br/>We build it.
            </h2>
            <p className="text-base leading-relaxed mb-8" style={{color:'var(--text-muted)'}}>
              Pick from our portfolio of coastal-ready home plans — engineered for the Gulf, sized for your family, and ready to break ground.
            </p>
            <Link to="/floor-plans" className="btn-primary" data-testid="browse-plans-btn">Browse Floor Plans <Compass size={16}/></Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {plans.map((p, i) => (
              <Link to={`/floor-plans/${p.id}`} key={p.id} className={`card overflow-hidden group ${i === 0 ? 'col-span-2' : ''}`} data-testid={`teaser-plan-${i}`}>
                <div className={`overflow-hidden ${i === 0 ? 'aspect-[16/9]' : 'aspect-[4/3]'}`}>
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <div className="font-display text-lg font-bold">{p.name}</div>
                  <div className="text-xs flex gap-3 mt-1" style={{color:'var(--text-muted)'}}>
                    <span><Ruler size={11} className="inline mr-1"/>{p.sqft.toLocaleString()} sqft</span>
                    <span>{p.bedrooms}br · {p.bathrooms}ba</span>
                    <span style={{color:'var(--accent)'}}>from {money(p.price_from)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="label-overline mb-4">From our clients</div>
        <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-12 max-w-2xl">Trusted by Coastal Bend homeowners.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.id} className="card p-7" data-testid={`testimonial-${t.id}`}>
              <div className="flex gap-1 mb-4">
                {Array.from({length: t.rating}).map((_, i) => <Star key={i} size={14} fill="#D4A373" stroke="none" />)}
              </div>
              <p className="text-[15px] leading-relaxed mb-6">&ldquo;{t.content}&rdquo;</p>
              <div className="text-sm font-semibold">{t.client_name}</div>
              <div className="text-xs" style={{color:'var(--text-muted)'}}>{t.location} · {t.project_type}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="relative overflow-hidden rounded-2xl p-10 lg:p-16" style={{background: `linear-gradient(135deg, var(--surface), var(--surface-2))`, border: '1px solid var(--border)'}}>
          <Sparkles size={140} className="absolute -right-6 -bottom-6 opacity-10" style={{color:'var(--accent)'}} />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-4">Let&rsquo;s build something that lasts.</h2>
            <p className="mb-8 text-base lg:text-lg" style={{color:'var(--text-muted)'}}>
              Tell us about your project. We&rsquo;ll respond within one business day with a clear path forward.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="cta-quote-btn">Request a Quote <ChevronRight size={16}/></Link>
              <a href="tel:+13618331309" className="btn-ghost"><Phone size={15}/> (361) 833-1309</a>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
